import { FC } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes
} from "react-router-dom";

import { useAuthContext } from "providers/Auth";

import { StatementsView } from "views/statements";
import { StatementsIdView } from "views/statements-id";
import { Dashboard } from "shared/app/Dashboard/Dashboard";
import { SignUpView } from "views/sign-up";
import { OnboardingView } from "views/onboarding";
import { NotFoundView } from "views/not-found";
import { Redirect } from "shared/components/Redirect";
import { CreditImpactView } from "views/credit-impact";
import { VerificationTemplatesView } from "views/verification-templates";
import { VerificationTemplatesIdView } from "views/verification-templates-id";
import { ApplicationsView } from "views/applications";
import { ApplicationsIdReportView } from "views/applications-id-report";
import { ApplicationsIdOverview } from "views/applications-id";
import { AddProperty } from "views/properties-add";
import { PropertyDetail } from "views/properties-id";
import { Properties } from "views/properties";
import { Units } from "views/units";
import { UnitDetail } from "views/properties-id-units-id";
import { AddUnitsView } from "views/properties-id-units-add";
import { Login } from "views/login";
import { ForgotPassword } from "views/forgot-password";
import { RentReportingView } from "views/rent-reporting";
import { RentReportingUpload } from "views/rent-reporting-upload";
import { SettingsView } from "views/settings";
import { Profile } from "views/settings-profile";
import { DevelopersView } from "views/settings-developers";
import { UsersView } from "views/settings-users";
import { AddNewSubscriber } from "views/rent-reporting-add";
import { SubscriptionDetails } from "views/rent-reporting-id";
import { EditSubscriptionDetails } from "views/rent-reporting-id-edit";
import { RentersPage } from "views/renters";
import { Loader } from "shared/components/Loader";
import { UserPermissionsView } from "views/settings-users-id";
import { ApplicationsIdReportPrintView } from "views/applications-id-report/print";
import { RentReportingUploadIdView } from "views/rent-reporting-upload-id";
import { RentReportingCsvAnalysisView } from "views/rent-reporting-upload-csv-analysis";
import { NotificationsView } from "views/settings-notifications";
import { TeamView } from "views/settings-team";
import { ResubscribeSubscriptionDetails } from "views/rent-reporting-id-resubscribe";
import { CreateNewSubscription } from "views/rent-reporting-id-new-subscribe";

export const RouterProvider: FC = () => {
  const { auth, permissions, type } = useAuthContext();

  if (auth === undefined)
    return (
      <div className="animate-appear pt-10 flex flex-col items-center">
        <Loader />
      </div>
    );

  return (
    <Router>
      <Routes>
        {!auth ? (
          <>
            <Route path="/forgot-password" element={<ForgotPassword />}></Route>
            <Route path="/sign-up" element={<SignUpView />}></Route>
            <Route path="/login" element={<Login />}></Route>
            <Route path="*" element={<Redirect href="/login" />}></Route>
          </>
        ) : auth.partner.status === "active" ? (
          <>
            <Route path="/" element={<Dashboard />}>
              {type.isAdmin || type.isOwner ? (
                <>
                  <Route path="renters" element={<RentersPage />} />
                  <Route path="credit-impact" element={<CreditImpactView />} />
                  <Route
                    path="rent-reporting"
                    element={<RentReportingView />}
                  />
                  <Route
                    path="rent-reporting/upload"
                    element={<RentReportingUpload />}
                  />
                  <Route
                    path="rent-reporting/upload/csv-analysis"
                    element={<RentReportingCsvAnalysisView />}
                  />
                  <Route
                    path="rent-reporting/upload/:id"
                    element={<RentReportingUploadIdView />}
                  />
                  <Route
                    path="rent-reporting/add"
                    element={<AddNewSubscriber />}
                  />
                  <Route
                    path="rent-reporting/:renterId"
                    element={<SubscriptionDetails />}
                  />
                  <Route
                    path="rent-reporting/:renterId/edit"
                    element={<EditSubscriptionDetails />}
                  />
                  <Route
                    path="rent-reporting/:renterId/resubscribe"
                    element={<ResubscribeSubscriptionDetails />}
                  />
                  <Route
                    path="rent-reporting/:renterId/new-subscription"
                    element={<CreateNewSubscription />}
                  />
                </>
              ) : null}
              <Route path="properties" element={<Properties />} />
              <Route path="applications" element={<ApplicationsView />} />
              <Route
                path="applications/:applicationId"
                element={<ApplicationsIdOverview />}
              />
              {type.isAdmin || type.isOwner || permissions.canViewReports ? (
                <>
                  <Route
                    path="applications/:applicationId/reports"
                    element={<ApplicationsIdReportView />}
                  />
                  <Route
                    path="applications/:applicationId/reports/print"
                    element={<ApplicationsIdReportPrintView />}
                  />
                </>
              ) : null}

              <Route path="properties/add" element={<AddProperty />} />
              <Route
                path="properties/:propertyId"
                element={<PropertyDetail />}
              />
              <Route
                path="properties/:propertyId/units/add"
                element={<AddUnitsView />}
              />
              <Route path="units" element={<Units />} />
              <Route
                path="properties/:propertyId/units/:unitId"
                element={<UnitDetail />}
              />
              {type.isAdmin || type.isOwner ? (
                <>
                  <Route
                    path="verification-templates"
                    element={<VerificationTemplatesView />}
                  />
                  <Route
                    path="verification-templates/:id"
                    element={<VerificationTemplatesIdView />}
                  />
                </>
              ) : null}
              <Route path="*" element={<NotFoundView />} />
            </Route>
            <Route path="settings" element={<Dashboard />}>
              <Route index element={<SettingsView />} />
              {type.isAdmin || type.isOwner ? (
                <>
                  <Route path="users" element={<UsersView />} />
                  <Route
                    path="users/:userId"
                    element={<UserPermissionsView />}
                  />
                  <Route path="team" element={<TeamView />} />
                  <Route path="developers" element={<DevelopersView />} />
                  <Route path="statements" element={<StatementsView />} />
                  <Route
                    path="statements/:statementId"
                    element={<StatementsIdView />}
                  />
                </>
              ) : null}
              <Route path="profile" element={<Profile />} />
              <Route path="notifications" element={<NotificationsView />} />
            </Route>
            {/* REDIRECTS */}
            <Route path="/login" element={<Navigate to="/" />} />
            <Route path="/forgot-password" element={<Navigate to="/" />} />
            <Route path="/sign-up" element={<Navigate to="/" />} />
            <Route path="/onboarding" element={<Navigate to="/" />} />
          </>
        ) : (
          <>
            <Route path="/onboarding" element={<OnboardingView />} />
            <Route path="*" element={<Redirect href="/onboarding" />} />
          </>
        )}
      </Routes>
    </Router>
  );
};
